'use client';

import { useTheme } from 'next-themes';
import { useEffect } from 'react';
import { THEME_COLOR } from '@/lib/theme-color';

/**
 * Keeps `<meta name="theme-color">` — the colour a mobile browser paints its
 * own chrome with — matching the page.
 *
 * `viewport.themeColor` in `app/layout.tsx` already covers the system
 * preference with two `media` entries and no JavaScript at all. This exists
 * only for the case that can't express: a viewer who has explicitly chosen
 * light or dark against their OS setting. It replaces an inline
 * `<Script strategy="beforeInteractive">` carrying a MutationObserver, which
 * React 19 warns about because a script tag rendered by a component never
 * executes on the client.
 */
export function ThemeColorSync() {
  const { theme, resolvedTheme } = useTheme();

  useEffect(() => {
    // "system" is already handled by the media-scoped tags, and overriding
    // them here would break a viewer whose OS flips while the tab is open.
    if (theme === 'system' || !resolvedTheme) return;
    const meta = document.querySelector(
      'meta[name="theme-color"]:not([media])',
    );
    if (meta) {
      meta.setAttribute(
        'content',
        resolvedTheme === 'dark' ? THEME_COLOR.dark : THEME_COLOR.light,
      );
    }
  }, [theme, resolvedTheme]);

  return null;
}
